import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import {
  Send, Mic, MicOff, RefreshCw, Building2, X, ChevronDown, ChevronUp,
  ChevronLeft, ChevronRight,
  MessageSquare, Zap, Clock, AlertTriangle, Loader2, Info, Globe2, Code2, Copy,
  Home, Building, LayoutGrid, User, Share2, Ruler, Coins, Calendar,
  Percent, Hash, Tag,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  PALETTE, STATUS_COLOR_MAP,
  formatPEN, formatNumber, formatPENCompact,
  tooltipDefaults, baseFont,
} from '../lib/chartConfig';

const API = process.env.REACT_APP_BACKEND_URL;

// ============ Comandos disponibles ============
// Orden ascendente /1 → /10. Renumeración v2 (post migración a comercial_v2).
const COMMANDS = [
  { id: '/1', label: 'Acumulado de ventas del mes', tooltip: 'Ventas vs Meta YTD · Opcional: /1 <mes> <año> · ej: /1 ene 2026 (default mes actual)' },
  { id: '/2', label: 'Detalle de recaudación', tooltip: 'Opcional: /2 <mes> <año> · ej: /2 ene 2026 (sin params: todo el histórico)' },
  { id: '/3', label: 'Detalle de unidades', tooltip: 'Opcional: /3 <mes> <año> · ej: /3 ene 2026 (sin params: todo el histórico)' },
  { id: '/4', label: 'Precio promedio de lista', tooltip: 'Promedio por tipo de unidad (lista de precios vigente)' },
  { id: '/5', label: 'Precio promedio m² de lista', tooltip: 'S/. por m² promedio por tipo (lista de precios vigente)' },
  { id: '/6', label: 'Tipologías más vendidas', tooltip: 'Opcional: /6 N · ej: /6 3 (default 5)' },
  { id: '/7', label: 'Mayor % y valor de dscto vendido' },
  { id: '/8', label: 'Precio promedio full descuento', tooltip: 'Promedio por tipo de unidad (full descuento agresivo)' },
  { id: '/9', label: 'Precio promedio m² con full descuento', tooltip: 'S/. por m² promedio por tipo (full descuento agresivo)' },
  { id: '/10', label: 'Status de unidades inmobiliarias' },
];

// Carrusel de comandos: 6 chips por página (3 filas x 2 cols).
const COMMANDS_PAGE_SIZE = 6;

const VOICE_NUMBER_MAP = {
  'uno': '/1', 'una': '/1', 'primero': '/1',
  'dos': '/2', 'segundo': '/2',
  'tres': '/3', 'tercero': '/3',
  'cuatro': '/4', 'cuarto': '/4',
  'cinco': '/5', 'quinto': '/5',
  'seis': '/6', 'sexto': '/6',
  'siete': '/7', 'séptimo': '/7',
  'ocho': '/8', 'octavo': '/8',
  'nueve': '/9', 'noveno': '/9',
  'diez': '/10', 'décimo': '/10', 'decimo': '/10',
};

// ============ Helpers de formato ============
// (formatPEN, formatNumber se importan desde lib/chartConfig)

// Formato fecha peruano dd/mm/yyyy a partir de strings ISO o yyyy-mm-dd
const formatDate = (raw) => {
  if (!raw) return '—';
  const s = String(raw).trim();
  // matches yyyy-mm-dd o yyyy-mm-ddThh:mm:ss
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
};

// Mapeo de iconos sugeridos por el backend
const ICON_COMPONENTS = {
  home: Home, building: Building, layout: LayoutGrid,
  user: User, share: Share2, ruler: Ruler, coins: Coins,
  calendar: Calendar, percent: Percent, hash: Hash, tag: Tag,
  info: Info,
};

// ============ Modal de contexto ============
function ContextModal({ onClose, onConfirm }) {
  const [projects, setProjects] = useState([]);
  const [stages, setStages] = useState([]);
  const [loadingP, setLoadingP] = useState(false);
  const [loadingS, setLoadingS] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedStage, setSelectedStage] = useState('');

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  // Cargar proyectos al montar (con cache en sessionStorage)
  useEffect(() => {
    let alive = true;
    const cached = sessionStorage.getItem('chatbot_projects_cache_v3');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setProjects(parsed);
          // No hacemos return: refrescamos en background sin loader
          axios.get(`${API}/api/chatbot/projects`, { headers: authHeaders })
            .then((r) => {
              if (!alive) return;
              const items = r.data.items || [];
              setProjects(items);
              sessionStorage.setItem('chatbot_projects_cache_v3', JSON.stringify(items));
            })
            .catch(() => { /* mantenemos cache */ });
          return () => { alive = false; };
        }
      } catch { /* ignore */ }
    }
    setLoadingP(true);
    setErrorMsg('');
    axios.get(`${API}/api/chatbot/projects`, { headers: authHeaders })
      .then((r) => {
        if (!alive) return;
        const items = r.data.items || [];
        setProjects(items);
        sessionStorage.setItem('chatbot_projects_cache_v3', JSON.stringify(items));
      })
      .catch((e) => {
        if (!alive) return;
        const detail = e?.response?.data?.detail || e.message || 'Error desconocido';
        setErrorMsg(`No se pudieron cargar los proyectos: ${detail}`);
      })
      .finally(() => { if (alive) setLoadingP(false); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar etapas cuando cambia proyecto
  useEffect(() => {
    if (!selectedProject) { setStages([]); setSelectedStage(''); return; }
    let alive = true;
    setLoadingS(true);
    axios.get(`${API}/api/chatbot/stages`, {
      headers: authHeaders,
      params: { codigo_proyecto: selectedProject },
    })
      .then((r) => { if (alive) setStages(r.data.items || []); })
      .catch(() => { if (alive) setStages([]); })
      .finally(() => { if (alive) setLoadingS(false); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const handleConfirm = () => {
    if (!selectedProject) {
      setErrorMsg('Debes seleccionar un proyecto.');
      return;
    }
    if (hasStages && !selectedStage) {
      setErrorMsg('Este proyecto tiene etapas. Debes seleccionar una etapa.');
      return;
    }
    const proj = projects.find((p) => p.codigo_proyecto === selectedProject);
    const stage = stages.find((s) => s.codigo_etapa === selectedStage) || null;
    onConfirm({ project: proj, stage });
  };

  // Etapas reales del proyecto: excluye el placeholder '0' ("Sin Etapas")
  // que algunos proyectos tienen en dim_etapa como convención cuando NO
  // están segmentados por etapa.
  const realStages = stages.filter((s) => s.codigo_etapa !== '0');
  // El proyecto tiene etapas reales solo si terminó de cargar y hay al menos 1.
  const hasStages = !loadingS && selectedProject !== '' && realStages.length > 0;
  // El proyecto NO tiene etapas (post-loading) — mostrar nota informativa.
  const noStages = !loadingS && selectedProject !== '' && realStages.length === 0;
  const stageMissing = hasStages && !selectedStage;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" data-testid="context-modal">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/20 hover:bg-white/40 text-white transition-colors"
          data-testid="context-modal-close-btn"
          aria-label="Cerrar"
        >
          <X size={18} />
        </button>
        <div className="bg-gradient-to-r from-[#9d8272] to-[#8a7162] p-5">
          <div className="flex items-center gap-3 text-white">
            <MessageSquare size={24} />
            <div>
              <h2 className="text-lg font-bold">Chatbot Comercial</h2>
              <p className="text-white/80 text-sm">Seleccione el contexto para iniciar</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {errorMsg && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2">
              <AlertTriangle size={16} className="text-red-500 mt-0.5 shrink-0" />
              <p className="text-xs text-red-700" data-testid="context-error">{errorMsg}</p>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-[#5A5A5A] mb-1.5 block">
              Proyecto Inmobiliario <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={selectedProject}
                onChange={(e) => {
                  setSelectedProject(e.target.value);
                  setSelectedStage('');
                  setErrorMsg('');
                }}
                className="w-full px-3 py-2.5 border border-[#E8E8E7] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#9d8272] bg-white disabled:opacity-60"
                disabled={loadingP || !!errorMsg}
                data-testid="context-project-select"
              >
                <option value="">{loadingP ? 'Cargando…' : 'Seleccionar proyecto...'}</option>
                {projects.map((p) => (
                  <option key={p.codigo_proyecto} value={p.codigo_proyecto}>
                    {p.nombre_proyecto} ({p.codigo_proyecto})
                  </option>
                ))}
              </select>
              {loadingP && <Loader2 size={14} className="absolute right-9 top-3 text-[#9d8272] animate-spin" />}
            </div>
          </div>
          {selectedProject && loadingS && (
            <div className="flex items-center gap-2 text-xs text-[#888]" data-testid="stages-loading">
              <Loader2 size={14} className="text-[#9d8272] animate-spin" />
              Cargando etapas…
            </div>
          )}
          {hasStages && (
            <div>
              <label className="text-xs font-semibold text-[#5A5A5A] mb-1.5 block">
                Etapa <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={selectedStage}
                  onChange={(e) => { setSelectedStage(e.target.value); setErrorMsg(''); }}
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#9d8272] bg-white ${stageMissing ? 'border-red-300' : 'border-[#E8E8E7]'}`}
                  data-testid="context-stage-select"
                >
                  <option value="">Selecciona una etapa...</option>
                  {realStages.map((s) => (
                    <option key={s.codigo_etapa} value={s.codigo_etapa}>
                      {s.nombre_etapa} ({s.codigo_etapa})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {noStages && (
            <div
              className="rounded-lg border border-[#e5ddd4] bg-[#f8f6f3] p-3 flex items-start gap-2"
              data-testid="context-no-stages-info"
            >
              <Info size={14} className="text-[#9d8272] mt-0.5 shrink-0" />
              <p className="text-xs text-[#5A5A5A] leading-relaxed">
                Este proyecto no tiene etapas. Puedes continuar — los comandos se aplicarán al proyecto completo.
              </p>
            </div>
          )}
        </div>
        <div className="px-6 pb-6">
          <Button
            onClick={handleConfirm}
            disabled={!selectedProject || loadingS || stageMissing}
            className="w-full bg-[#9d8272] hover:bg-[#8a7162] text-white py-2.5"
            data-testid="context-confirm-btn"
          >
            Iniciar Conversación
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============ Renderers de payload ============
function MetricCard({ data }) {
  const fmt = data.value_format || (data.currency ? 'currency' : 'number');
  let display = '—';
  if (typeof data.value === 'number') {
    if (fmt === 'percent') {
      const v2 = data.value > 1 ? data.value / 100 : data.value;
      display = `${(v2 * 100).toFixed(2)}%`;
    } else if (fmt === 'currency') {
      display = `${data.currency || 'S/.'} ${formatNumber(data.value, 0)}${data.unit || ''}`;
    } else if (fmt === 'integer') {
      display = formatNumber(Math.round(data.value), 0);
    } else if (fmt === 'number') {
      display = Number.isInteger(data.value)
        ? formatNumber(data.value, 0)
        : formatNumber(data.value, 2);
    } else {
      display = String(data.value);
    }
  } else if (data.value !== null && data.value !== undefined) {
    display = String(data.value);
  }
  return (
    <div className="bg-gradient-to-br from-[#f8f6f3] to-[#f0ebe5] rounded-xl p-5 border border-[#e5ddd4]" data-testid="response-metric">
      <p className="text-xs font-medium text-[#9d8272] uppercase tracking-wider mb-2">{data.title}</p>
      <p className="text-3xl font-bold text-[#2D2D2D] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{display}</p>
      <p className="text-sm text-[#5A5A5A]">{data.subtitle}</p>
    </div>
  );
}

function MetricMultiCard({ data }) {
  return (
    <div className="bg-gradient-to-br from-[#f8f6f3] to-[#f0ebe5] rounded-xl p-5 border border-[#e5ddd4]" data-testid="response-metric-multi">
      <p className="text-xs font-medium text-[#9d8272] uppercase tracking-wider mb-3">{data.title}</p>
      <div className="grid grid-cols-3 gap-3 mb-2">
        {data.kpis.map((kpi, i) => {
          const v = typeof kpi.value === 'number'
            ? `${kpi.currency || ''} ${formatNumber(kpi.value, 0)}`.trim()
            : kpi.value;
          return (
            <div key={i} className="bg-white rounded-lg p-3 border border-[#e5ddd4]">
              <p className="text-[10px] text-[#888] uppercase tracking-wider mb-1">{kpi.label}</p>
              <p className="text-lg font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>{v}</p>
            </div>
          );
        })}
      </div>
      {data.subtitle && <p className="text-sm text-[#5A5A5A]">{data.subtitle}</p>}
    </div>
  );
}

// Tarjeta KPI desglosada por tipo de unidad (/4, /5, /8, /9 — v2).
// Payload: { type:'metric_by_tipo', title, subtitle, currency, unit,
//            items: [{ tipo, valor, n, n_validos,
//                      por_tipologia: [{ tipologia, dormitorios, valor, n, n_validos }] }] }
function MetricByTipoCard({ data }) {
  const items = Array.isArray(data.items) ? data.items : [];
  const cur = data.currency || 'S/.';
  const unit = data.unit || '';

  const fmtValor = (v) => {
    const numericV = typeof v === 'number' ? v : Number(v);
    const validNum = Number.isFinite(numericV) && numericV > 0;
    return validNum ? `${cur} ${formatNumber(numericV, 0)}${unit}` : '—';
  };

  return (
    <div
      className="bg-gradient-to-br from-[#f8f6f3] to-[#f0ebe5] rounded-xl p-5 border border-[#e5ddd4]"
      data-testid="response-metric-by-tipo"
    >
      <p className="text-xs font-medium text-[#9d8272] uppercase tracking-wider mb-3">
        {data.title}
      </p>
      {items.length === 0 ? (
        <p className="text-sm text-[#888]">Sin datos disponibles para este proyecto/etapa.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-2">
          {items.map((it, i) => {
            const numericV = typeof it.valor === 'number' ? it.valor : Number(it.valor);
            const validNum = Number.isFinite(numericV) && numericV > 0;
            const display = validNum
              ? `${cur} ${formatNumber(numericV, 0)}${unit}`
              : '—';
            const sinDatos = it.n_validos === 0 || !validNum;
            const detalle = Array.isArray(it.por_tipologia) ? it.por_tipologia : [];
            const tipoKey = (it.tipo || '').toLowerCase();
            return (
              <div
                key={it.tipo || i}
                className="bg-white rounded-lg p-3 border border-[#e5ddd4] flex flex-col"
                data-testid={`metric-by-tipo-${tipoKey}`}
              >
                <p className="text-[10px] text-[#888] uppercase tracking-wider mb-1">{it.tipo}</p>
                <p
                  className="text-xl font-bold text-[#2D2D2D]"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {display}
                </p>
                <p className="text-[11px] text-[#888] mt-1">
                  {sinDatos
                    ? <span className="text-amber-600">Sin datos válidos</span>
                    : `${formatNumber(it.n_validos, 0)} de ${formatNumber(it.n, 0)} unidades`}
                </p>

                {detalle.length > 0 && (
                  <div
                    className="mt-3 pt-3 border-t border-[#ede5db]"
                    data-testid={`tipologia-detail-${tipoKey}`}
                  >
                    <p className="text-[9px] text-[#9d8272] uppercase tracking-wider mb-2">
                      Detalle por tipología
                    </p>
                    <div className="overflow-hidden rounded-md border border-[#f0ebe5]">
                      <table className="w-full text-[11px]">
                        <thead className="bg-[#f8f6f3]">
                          <tr>
                            <th className="text-left px-2 py-1 font-medium text-[#9d8272]">Tipol.</th>
                            <th className="text-center px-2 py-1 font-medium text-[#9d8272]">Dorm.</th>
                            <th className="text-right px-2 py-1 font-medium text-[#9d8272]">Prom.</th>
                            <th className="text-right px-2 py-1 font-medium text-[#9d8272]">n</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detalle.map((d, idx) => (
                            <tr
                              key={`${d.tipologia}-${d.dormitorios}-${idx}`}
                              className="border-t border-[#f0ebe5]"
                            >
                              <td className="px-2 py-1 text-[#2D2D2D] font-medium">{d.tipologia || '—'}</td>
                              <td className="px-2 py-1 text-center text-[#5A5A5A]">
                                {d.dormitorios !== null && d.dormitorios !== undefined ? d.dormitorios : '—'}
                              </td>
                              <td className="px-2 py-1 text-right text-[#2D2D2D]">{fmtValor(d.valor)}</td>
                              <td className="px-2 py-1 text-right text-[#888]">
                                {formatNumber(d.n_validos, 0)}/{formatNumber(d.n, 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {data.subtitle && <p className="text-sm text-[#5A5A5A] mt-2">{data.subtitle}</p>}
    </div>
  );
}

const STATUS_COLOR = {
  V: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', dot: PALETTE.vendido },
  S: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', dot: PALETTE.separado },
  D: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', dot: PALETTE.disponible },
  B: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300', dot: PALETTE.bloqueado },
  P: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', dot: PALETTE.preventa },
};

function StatusChart({ data }) {
  // Compat con respuesta nueva (con `by_tipo`) y legacy (`items` directo)
  const tipos = data.tipos && data.tipos.length > 0 ? data.tipos : null;
  const defaultTipo = data.default_tipo || (tipos ? tipos[0] : null);
  const [tipoActivo, setTipoActivo] = useState(defaultTipo);

  // Bucket activo (items + total) según el tipo seleccionado o legacy
  const activeBucket = useMemo(() => {
    if (tipos && data.by_tipo && tipoActivo && data.by_tipo[tipoActivo]) {
      return data.by_tipo[tipoActivo];
    }
    return { items: data.items || [], total_unidades: data.total_unidades || 0 };
  }, [data, tipos, tipoActivo]);

  const items = activeBucket.items || [];
  const total = activeBucket.total_unidades
    || items.reduce((a, i) => a + (i.unidades || 0), 0);
  const showPills = !!tipos && tipos.length > 1;

  const doughnutData = useMemo(() => ({
    labels: items.map((i) => `${i.desc} [${i.codigo}]`),
    datasets: [{
      data: items.map((i) => i.unidades || 0),
      backgroundColor: items.map((i) => STATUS_COLOR_MAP[i.codigo] || PALETTE.bloqueado),
      borderColor: '#fff',
      borderWidth: 2,
      hoverOffset: 6,
    }],
  }), [items]);

  const doughnutOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    plugins: {
      legend: {
        position: 'right',
        labels: {
          font: baseFont,
          color: PALETTE.textSecondary,
          boxWidth: 12,
          padding: 8,
        },
      },
      tooltip: {
        ...tooltipDefaults,
        callbacks: {
          label: (ctx) => {
            const item = items[ctx.dataIndex];
            return `${formatNumber(item.unidades, 0)} unid. (${formatNumber(item.pct, 2)}%) · ${formatPEN(item.valor)}`;
          },
        },
      },
    },
  }), [items]);

  return (
    <div className="rounded-xl border border-[#E8E8E7] overflow-hidden bg-white" data-testid="response-status">
      <div className="bg-[#f8f6f3] px-4 py-2.5 border-b border-[#E8E8E7] flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-semibold text-[#2D2D2D]">
          {showPills && tipoActivo ? `${tipoActivo} — ${data.title}` : data.title}
        </p>
        <p className="text-xs text-[#888]">Total: {formatNumber(total, 0)} unid.</p>
      </div>

      {showPills && (
        <div className="px-4 pt-3 flex flex-wrap gap-1.5" data-testid="status-tipo-pills">
          {tipos.map((t) => {
            const isActive = t === tipoActivo;
            const tipoTotal = data.by_tipo?.[t]?.total_unidades || 0;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTipoActivo(t)}
                className={`text-xs px-3 py-1 rounded-full border transition ${
                  isActive
                    ? 'bg-[#9d8272] text-white border-[#9d8272]'
                    : 'bg-[#f8f6f3] text-[#5A5A5A] border-[#E8E8E7] hover:bg-[#ece5d8]'
                }`}
                data-testid={`status-tipo-pill-${t}`}
              >
                {t} ({formatNumber(tipoTotal, 0)})
              </button>
            );
          })}
        </div>
      )}

      <div className="p-4" style={{ height: 220 }}>
        <Doughnut data={doughnutData} options={doughnutOptions} />
      </div>
      <div className="px-4 pb-4 space-y-2">
        {items.map((item) => {
          const color = STATUS_COLOR[item.codigo] || STATUS_COLOR.B;
          return (
            <div key={item.codigo} className={`rounded-lg border ${color.border} ${color.bg} p-2.5`} data-testid={`status-chip-${item.codigo}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: color.dot }} />
                  <span className={`text-xs font-semibold ${color.text}`}>{item.desc}</span>
                  <span className="text-[10px] uppercase tracking-wide text-[#aaa]">[{item.codigo}]</span>
                </div>
                <span className={`text-xs font-bold ${color.text}`}>{formatNumber(item.unidades, 0)} ({formatNumber(item.pct, 2)}%)</span>
              </div>
              <p className="text-[10px] text-[#666]">Valor lista: {formatPEN(item.valor)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SalesBarChart({ data }) {
  const meses = data.meses || [];
  const { chartData, chartOptions } = useMemo(() => {
    const labels = meses.map((m) => m.mes_label);
    const ventas = meses.map((m) => m.ventas || 0);
    const meta = meses.map((m) => m.meta || 0);
    const hasMeta = meta.some((m) => m > 0);

    const datasets = [{
      label: 'Venta',
      data: ventas,
      backgroundColor: PALETTE.primary,
      borderColor: PALETTE.primaryDark,
      borderWidth: 1,
      borderRadius: 6,
      maxBarThickness: 36,
    }];
    if (hasMeta) {
      datasets.push({
        label: 'Meta',
        data: meta,
        backgroundColor: PALETTE.meta,
        borderColor: PALETTE.metaBorder,
        borderWidth: 1,
        borderRadius: 6,
        maxBarThickness: 36,
      });
    }

    return {
      chartData: { labels, datasets },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600, easing: 'easeOutQuart' },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              font: baseFont,
              color: PALETTE.textSecondary,
              boxWidth: 12,
              padding: 8,
              usePointStyle: true,
              pointStyle: 'rectRounded',
            },
          },
          tooltip: {
            ...tooltipDefaults,
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${formatPEN(ctx.parsed.y)}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: baseFont, color: PALETTE.textSecondary },
          },
          y: {
            beginAtZero: true,
            grid: { color: '#f0f0f0', drawBorder: false },
            ticks: {
              font: baseFont,
              color: PALETTE.textMuted,
              callback: (v) => formatPENCompact(v),
            },
          },
        },
      },
    };
  }, [meses]);

  const pct = data.pct_cumplimiento;
  const pctLabel = (pct === null || pct === undefined)
    ? '—'
    : `${(pct * 100).toFixed(1)}%`;

  return (
    <div className="rounded-xl border border-[#E8E8E7] overflow-hidden bg-white" data-testid="response-sales-chart">
      <div className="bg-[#f8f6f3] px-4 py-2.5 border-b border-[#E8E8E7]">
        <p className="text-sm font-semibold text-[#2D2D2D]">{data.title}</p>
      </div>
      <div className="p-4">
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-[#f8f6f3] rounded-lg p-2 text-center" data-testid="sales-kpi-acumulado">
            <p className="text-[10px] text-[#888] uppercase">Acumulado YTD</p>
            <p className="text-base font-bold text-[#2D2D2D]">{formatPEN(data.acumulado)}</p>
          </div>
          <div className="bg-[#f8f6f3] rounded-lg p-2 text-center" data-testid="sales-kpi-meta">
            <p className="text-[10px] text-[#888] uppercase">Meta YTD</p>
            <p className="text-base font-bold text-[#2D2D2D]">{formatPEN(data.meta)}</p>
          </div>
          <div className="bg-[#f8f6f3] rounded-lg p-2 text-center" data-testid="sales-kpi-pct">
            <p className="text-[10px] text-[#888] uppercase">% Cumplimiento</p>
            <p className="text-base font-bold text-[#2D2D2D]">{pctLabel}</p>
          </div>
        </div>
        {data.disclaimer_meta_faltante && data.disclaimer_text && (
          <div
            className="mb-3 px-3 py-2 rounded-md bg-[#fff7ed] border border-[#fed7aa] text-[11px] text-[#9a3412]"
            data-testid="sales-meta-disclaimer"
          >
            {data.disclaimer_text}
          </div>
        )}
        <div style={{ height: 240 }} aria-label={`Gráfico de ventas vs meta: ${data.title}. Acumulado ${formatPEN(data.acumulado)}.`}>
          <Bar data={chartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
}

// Formateador estilo es-PE: puntos como separador de miles (S/. 51.918.135)
const formatPEN_es = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return 'S/. -';
  const v = Math.round(Number(n));
  if (v === 0) return 'S/. -';
  return 'S/. ' + v.toLocaleString('es-PE', { maximumFractionDigits: 0 });
};

const formatPctES = (p) => {
  if (p === null || p === undefined || Number.isNaN(Number(p))) return '-';
  return `${(Number(p) * 100).toFixed(2)}%`;
};

const formatFechaES = (iso) => {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
};

function RecaudacionTable({ data }) {
  const columnas = data.columnas || [];
  const filas = data.filas || [];
  const fechaCorteFmt = formatFechaES(data.fecha_corte);

  return (
    <div
      className="rounded-xl border-2 border-[#2D2D2D] overflow-hidden bg-white shadow-sm"
      data-testid="recaudacion-table-root"
    >
      {/* Header */}
      <div className="bg-[#2D2D2D] text-white px-4 py-2.5 flex items-baseline justify-between gap-3">
        <p className="text-sm font-bold tracking-wide uppercase">
          {data.title || 'DETALLE RECAUDACIÓN'}
        </p>
        <p
          className="text-xs font-mono text-[#c5b8a2]"
          data-testid="recaudacion-fecha-corte"
        >
          ({fechaCorteFmt})
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="recaudacion-table">
          {/* Columnas: Detalle + V/P/S/D/TOTAL */}
          <thead>
            <tr className="bg-[#5A5A5A] text-white">
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase border-r border-[#3D3D3D] sticky left-0 bg-[#5A5A5A] z-10">
                Detalle
              </th>
              {columnas.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-2 text-center text-xs font-semibold border-r border-[#3D3D3D] last:border-r-0 ${
                    c.key === 'TOTAL' ? 'bg-[#3D3D3D]' : ''
                  }`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
            {/* Sub-header con la letra de status (V/P/S/D/-) */}
            <tr className="bg-[#7A7A7A] text-white">
              <th className="px-3 py-1.5 text-left text-[10px] font-medium uppercase tracking-wider sticky left-0 bg-[#7A7A7A] z-10 border-r border-[#5A5A5A]">
                Status
              </th>
              {columnas.map((c) => (
                <th
                  key={`sub-${c.key}`}
                  className="px-3 py-1.5 text-center text-base font-bold border-r border-[#5A5A5A] last:border-r-0"
                >
                  {c.key === 'TOTAL' ? '—' : c.key}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filas.map((f) => {
              const isTotal = !!f.is_total;
              const rowBg = isTotal ? 'bg-[#f0ece4]' : 'bg-white';
              const labelBg = isTotal ? 'bg-[#e5dfd2]' : 'bg-[#f8f6f3]';
              const fontWeight = isTotal ? 'font-bold' : 'font-medium';
              const borderTop = isTotal ? 'border-t-2 border-[#2D2D2D]' : '';

              return (
                <React.Fragment key={f.tipo}>
                  {/* Fila de montos */}
                  <tr
                    className={`${rowBg} ${borderTop}`}
                    data-testid={`recaudacion-row-${f.tipo}`}
                  >
                    <td
                      className={`px-3 py-2 text-xs uppercase tracking-wider ${fontWeight} text-[#2D2D2D] sticky left-0 ${labelBg} z-10 border-r border-[#E8E8E7]`}
                    >
                      {f.tipo}
                    </td>
                    {columnas.map((c) => {
                      const monto = f.montos?.[c.key];
                      const isTotalCol = c.key === 'TOTAL';
                      return (
                        <td
                          key={`${f.tipo}-${c.key}`}
                          className={`px-3 py-2 text-right text-xs font-mono tabular-nums border-r border-[#E8E8E7] last:border-r-0 ${
                            isTotalCol ? 'font-bold text-[#2D2D2D]' : 'text-[#3D3D3D]'
                          }`}
                          data-testid={`recaudacion-cell-${f.tipo}-${c.key}`}
                        >
                          {formatPEN_es(monto)}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Sub-fila de % */}
                  <tr
                    className={`${rowBg} border-b border-[#E8E8E7]`}
                    data-testid={`recaudacion-row-${f.tipo}-pct`}
                  >
                    <td
                      className={`px-3 py-1 text-[10px] text-[#7A7A7A] sticky left-0 ${labelBg} z-10 border-r border-[#E8E8E7]`}
                    >
                      %
                    </td>
                    {columnas.map((c) => {
                      const pct = f.pcts?.[c.key];
                      const showPct = c.key !== 'TOTAL' && pct !== undefined;
                      return (
                        <td
                          key={`${f.tipo}-${c.key}-pct`}
                          className="px-3 py-1 text-right text-[10px] font-mono tabular-nums text-[#7A7A7A] border-r border-[#E8E8E7] last:border-r-0"
                          data-testid={`recaudacion-pct-${f.tipo}-${c.key}`}
                        >
                          {showPct ? formatPctES(pct) : '-'}
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer mes/proyectado */}
      {data.mes_label && (
        <div className="bg-[#faf9f7] px-4 py-2 text-[11px] text-[#5A5A5A] border-t border-[#E8E8E7] flex justify-between items-center">
          <span>Acumulado al cierre de <strong className="text-[#2D2D2D]">{data.mes_label}</strong></span>
          <span className="font-mono text-[10px]">V·P·S desde fact_ventas · D (incluye Bloqueado) desde fact_disponibilidad</span>
        </div>
      )}
    </div>
  );
}


// Formato entero o 1 decimal: 0 entero, 0.6 decimal, 1.3 decimal
const formatRatioOrInt = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '0';
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const formatIntES = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '0';
  return String(Math.round(Number(v)));
};

function UnidadesTable({ data }) {
  const columnas = data.columnas || [];
  const filas = data.filas || [];
  const ratios = data.ratios || [];
  const fechaCorteFmt = formatFechaES(data.fecha_corte);

  return (
    <div
      className="rounded-xl border-2 border-[#2D2D2D] overflow-hidden bg-white shadow-sm"
      data-testid="unidades-table-root"
    >
      {/* Header */}
      <div className="bg-[#2D2D2D] text-white px-4 py-2.5 flex items-baseline justify-between gap-3">
        <p className="text-sm font-bold tracking-wide uppercase">
          {data.title || 'DETALLE UNIDADES'}
        </p>
        <p
          className="text-xs font-mono text-[#c5b8a2]"
          data-testid="unidades-fecha-corte"
        >
          ({fechaCorteFmt})
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="unidades-table">
          <thead>
            <tr className="bg-[#5A5A5A] text-white">
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase border-r border-[#3D3D3D] sticky left-0 bg-[#5A5A5A] z-10">
                Detalle
              </th>
              {columnas.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-2 text-center text-xs font-semibold border-r border-[#3D3D3D] last:border-r-0 ${
                    c.key === 'TOTAL' ? 'bg-[#3D3D3D]' : ''
                  }`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
            <tr className="bg-[#7A7A7A] text-white">
              <th className="px-3 py-1.5 text-left text-[10px] font-medium uppercase tracking-wider sticky left-0 bg-[#7A7A7A] z-10 border-r border-[#5A5A5A]">
                Status
              </th>
              {columnas.map((c) => (
                <th
                  key={`sub-${c.key}`}
                  className="px-3 py-1.5 text-center text-base font-bold border-r border-[#5A5A5A] last:border-r-0"
                >
                  {c.key === 'TOTAL' ? '—' : c.key}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filas.map((f) => {
              const tieneDatos = f.pcts !== null && f.pcts !== undefined;
              return (
                <React.Fragment key={f.tipo}>
                  {/* Fila de cantidades */}
                  <tr
                    className="bg-white"
                    data-testid={`unidades-row-${f.tipo}`}
                  >
                    <td className="px-3 py-2 text-xs uppercase tracking-wider font-medium text-[#2D2D2D] sticky left-0 bg-[#f8f6f3] z-10 border-r border-[#E8E8E7]">
                      {f.tipo}
                    </td>
                    {columnas.map((c) => {
                      const cantidad = f.cantidades?.[c.key];
                      const isTotalCol = c.key === 'TOTAL';
                      return (
                        <td
                          key={`${f.tipo}-${c.key}`}
                          className={`px-3 py-2 text-right text-xs font-mono tabular-nums border-r border-[#E8E8E7] last:border-r-0 ${
                            isTotalCol ? 'font-bold text-[#2D2D2D]' : 'text-[#3D3D3D]'
                          }`}
                          data-testid={`unidades-cell-${f.tipo}-${c.key}`}
                        >
                          {formatIntES(cantidad)}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Sub-fila % */}
                  <tr
                    className="bg-white border-b border-[#E8E8E7]"
                    data-testid={`unidades-row-${f.tipo}-pct`}
                  >
                    <td className="px-3 py-1 text-[10px] text-[#7A7A7A] sticky left-0 bg-[#f8f6f3] z-10 border-r border-[#E8E8E7]">
                      %
                    </td>
                    {columnas.map((c) => {
                      const pct = f.pcts?.[c.key];
                      const isTotalCol = c.key === 'TOTAL';
                      let display = '-';
                      if (tieneDatos) {
                        if (isTotalCol) {
                          display = '100%';
                        } else if (pct !== undefined && pct !== null) {
                          display = formatPctES(pct);
                        }
                      }
                      return (
                        <td
                          key={`${f.tipo}-${c.key}-pct`}
                          className={`px-3 py-1 text-right text-[10px] font-mono tabular-nums border-r border-[#E8E8E7] last:border-r-0 ${
                            isTotalCol && tieneDatos ? 'font-bold text-[#2D2D2D]' : 'text-[#7A7A7A]'
                          }`}
                          data-testid={`unidades-pct-${f.tipo}-${c.key}`}
                        >
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}

            {/* Filas de Ratios — separación visual */}
            {ratios.map((r, idx) => (
              <tr
                key={r.label}
                className={`bg-[#faf9f7] ${idx === 0 ? 'border-t-2 border-[#2D2D2D]' : 'border-t border-[#E8E8E7]'}`}
                data-testid={`unidades-row-${r.label.replace(/\s+/g, '-')}`}
              >
                <td className="px-3 py-2 text-xs uppercase tracking-wider font-semibold text-[#2D2D2D] sticky left-0 bg-[#ece6d8] z-10 border-r border-[#E8E8E7]">
                  {r.label}
                </td>
                {columnas.map((c) => (
                  <td
                    key={`${r.label}-${c.key}`}
                    className={`px-3 py-2 text-right text-xs font-mono tabular-nums border-r border-[#E8E8E7] last:border-r-0 ${
                      c.key === 'TOTAL' ? 'font-bold text-[#2D2D2D]' : 'text-[#3D3D3D]'
                    }`}
                    data-testid={`unidades-ratio-${r.label.replace(/\s+/g, '-')}-${c.key}`}
                  >
                    {formatRatioOrInt(r.valores?.[c.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {data.mes_label && (
        <div className="bg-[#faf9f7] px-4 py-2 text-[11px] text-[#5A5A5A] border-t border-[#E8E8E7] flex justify-between items-center">
          <span>Snapshot al cierre de <strong className="text-[#2D2D2D]">{data.mes_label}</strong></span>
          <span className="font-mono text-[10px]">V·P·S desde fact_ventas · D (incluye Bloqueado) desde fact_disponibilidad</span>
        </div>
      )}
    </div>
  );
}


function RankingTable({ data }) {
  return (
    <div className="rounded-xl border border-[#E8E8E7] overflow-hidden bg-white" data-testid="response-ranking">
      <div className="bg-[#f8f6f3] px-4 py-2.5 border-b border-[#E8E8E7]">
        <p className="text-sm font-semibold text-[#2D2D2D]">{data.title}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#faf9f7]">
              {data.columns.map((c) => (
                <th key={c.key} className="px-3 py-2 text-left text-xs font-semibold text-[#5A5A5A] border-b border-[#E8E8E7] whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && (
              <tr><td className="px-3 py-3 text-center text-[#888] text-xs" colSpan={data.columns.length}>Sin datos para los criterios indicados.</td></tr>
            )}
            {data.rows.map((row, i) => (
              <tr key={i} className="border-b border-[#f0f0f0] last:border-0 hover:bg-[#faf9f7]">
                {data.columns.map((c) => {
                  const v = row[c.key];
                  let formatted = v ?? '—';
                  if (v !== null && v !== undefined) {
                    if (c.format === 'currency') formatted = formatPEN(v);
                    else if (c.format === 'percent' && typeof v === 'number') {
                      const v2 = v > 1 ? v / 100 : v; // guard
                      formatted = `${(v2 * 100).toFixed(2)}%`;
                    }
                    else if (c.format === 'integer' && (typeof v === 'number' || !Number.isNaN(Number(v))))
                      formatted = formatNumber(Math.round(Number(v)), 0);
                    else if (c.format === 'number') formatted = formatNumber(v, 2);
                    else if (typeof v === 'number') formatted = formatNumber(v, 0);
                  }
                  return (
                    <td key={c.key} className={`px-3 py-2 text-sm whitespace-nowrap ${c.key === 'tipologia' ? 'font-medium text-[#2D2D2D]' : 'text-[#5A5A5A]'}`}>{formatted}</td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GenericTable({ data }) {
  const cols = data.columns || [];
  const rows = data.rows || [];
  return (
    <div className="rounded-xl border border-[#E8E8E7] overflow-hidden bg-white" data-testid="response-generic">
      <div className="bg-[#f8f6f3] px-4 py-2.5 border-b border-[#E8E8E7]">
        <p className="text-sm font-semibold text-[#2D2D2D]">{data.title}</p>
      </div>
      <div className="overflow-x-auto max-h-80">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#faf9f7]">
            <tr>
              {cols.map((c) => (
                <th key={c.key} className="px-3 py-2 text-left text-xs font-semibold text-[#5A5A5A] border-b border-[#E8E8E7] whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td className="px-3 py-3 text-center text-[#888] text-xs" colSpan={cols.length}>Sin resultados.</td></tr>
            )}
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-[#f0f0f0] last:border-0 hover:bg-[#faf9f7]">
                {cols.map((c) => {
                  const v = row[c.key];
                  let formatted = v;
                  if (v === null || v === undefined) formatted = '—';
                  else if (c.format === 'currency') formatted = formatPEN(v);
                  else if (c.format === 'percent' && typeof v === 'number') {
                    const v2 = v > 1 ? v / 100 : v; // guard: si ya viene > 1 normalizar
                    formatted = `${(v2 * 100).toFixed(2)}%`;
                  }
                  else if (c.format === 'integer' && (typeof v === 'number' || !Number.isNaN(Number(v))))
                    formatted = formatNumber(Math.round(Number(v)), 0);
                  else if (c.format === 'number') formatted = formatNumber(v, 2);
                  else if (typeof v === 'number') formatted = formatNumber(v, 0);
                  return (
                    <td key={c.key} className="px-3 py-2 text-sm text-[#5A5A5A] whitespace-nowrap">{String(formatted)}</td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 0 && (
        <div className="bg-[#f8f6f3] px-4 py-1.5 border-t border-[#E8E8E7] flex items-center justify-between flex-wrap gap-1">
          <p className="text-[10px] text-[#888]">{rows.length} fila{rows.length === 1 ? '' : 's'}</p>
          {/* Leyenda automática para tablas de disponibilidad */}
          {cols.some((c) => c.key === 'disponibles' || c.key === 'bloqueados') && (
            <p
              className="text-[10px] text-[#9d8272] italic"
              data-testid="generic-table-stock-legend"
            >
              📍 Stock = Disponibles + Bloqueados (ambos cuentan como inventario activo)
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function CannotAnswerCard({ data }) {
  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid="response-cannot-answer">
      <div className="flex items-start gap-2">
        <Info size={18} className="text-blue-500 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-blue-700 mb-1">{data.title}</p>
          <p className="text-sm text-blue-700">{data.message}</p>
        </div>
      </div>
    </div>
  );
}

function EmptyCard({ data }) {
  return (
    <div className="rounded-xl border border-[#E8E8E7] bg-[#faf9f7] p-4" data-testid="response-empty">
      <p className="text-sm font-semibold text-[#2D2D2D] mb-1">{data.title}</p>
      <p className="text-sm text-[#888]">{data.message}</p>
    </div>
  );
}

function MetricWithContext({ data }) {
  const kpi = data.kpi || {};
  const fmt = kpi.value_format || (kpi.currency ? 'currency' : 'number');
  let kpiDisplay = '—';
  if (typeof kpi.value === 'number') {
    if (fmt === 'percent') {
      const v2 = kpi.value > 1 ? kpi.value / 100 : kpi.value;
      kpiDisplay = `${(v2 * 100).toFixed(2)}%`;
    } else if (fmt === 'currency') {
      kpiDisplay = `${kpi.currency || 'S/.'} ${formatNumber(kpi.value, 0)}`;
    } else if (fmt === 'integer') {
      kpiDisplay = formatNumber(Math.round(kpi.value), 0);
    } else if (fmt === 'number') {
      kpiDisplay = Number.isInteger(kpi.value)
        ? formatNumber(kpi.value, 0)
        : formatNumber(kpi.value, 2);
    } else {
      kpiDisplay = String(kpi.value);
    }
  } else if (kpi.value !== null && kpi.value !== undefined) {
    kpiDisplay = String(kpi.value);
  }

  const renderContextValue = (item) => {
    const v = item.value;
    if (v === null || v === undefined || v === '') return '—';
    if (item.format === 'currency' && typeof v === 'number') return formatPEN(v);
    if (item.format === 'percent' && typeof v === 'number') {
      const v2 = v > 1 ? v / 100 : v;
      return `${(v2 * 100).toFixed(2)}%`;
    }
    if (item.format === 'integer' && (typeof v === 'number' || !Number.isNaN(Number(v)))) {
      // Para identificadores (n_dpto, cochera, etc.) NO usamos separador de miles
      return String(Math.round(Number(v)));
    }
    if (item.format === 'number' && typeof v === 'number') {
      return Number.isInteger(v) ? formatNumber(v, 0) : formatNumber(v, 2);
    }
    if (item.format === 'date') return formatDate(v);
    return String(v);
  };

  return (
    <div className="rounded-xl border border-[#e5ddd4] overflow-hidden bg-white" data-testid="response-metric-context">
      <div className="bg-gradient-to-br from-[#f8f6f3] to-[#f0ebe5] p-5">
        <p className="text-xs font-medium text-[#9d8272] uppercase tracking-wider mb-2">{data.title}</p>
        <p className="text-3xl font-bold text-[#2D2D2D] mb-0" style={{ fontFamily: 'Playfair Display, serif' }}>{kpiDisplay}</p>
        {kpi.label && (
          <p className="text-xs text-[#9d8272] mt-1">{kpi.label}</p>
        )}
      </div>
      {data.context && data.context.length > 0 && (
        <div className="divide-y divide-[#f0ebe5]" data-testid="metric-context-list">
          {data.context.map((item, i) => {
            const Icon = ICON_COMPONENTS[item.icon] || Info;
            return (
              <div key={i} className="flex items-center justify-between px-4 py-2.5 hover:bg-[#faf9f7] transition-colors" data-testid={`metric-context-${item.key}`}>
                <span className="text-xs text-[#5A5A5A] flex items-center gap-2">
                  <Icon size={14} className="text-[#9d8272] shrink-0" />
                  {item.label}
                </span>
                <span className="text-sm font-medium text-[#2D2D2D] text-right ml-3">{renderContextValue(item)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ResponseRenderer({ payload }) {
  if (!payload) return null;
  switch (payload.type) {
    case 'metric': return <MetricCard data={payload} />;
    case 'metric_multi': return <MetricMultiCard data={payload} />;
    case 'metric_by_tipo': return <MetricByTipoCard data={payload} />;
    case 'metric_with_context': return <MetricWithContext data={payload} />;
    case 'status_chart': return <StatusChart data={payload} />;
    case 'sales_chart': return <SalesBarChart data={payload} />;
    case 'recaudacion_table': return <RecaudacionTable data={payload} />;
    case 'unidades_table': return <UnidadesTable data={payload} />;
    case 'ranking_table': return <RankingTable data={payload} />;
    case 'generic_table': return <GenericTable data={payload} />;
    case 'cannot_answer': return <CannotAnswerCard data={payload} />;
    case 'empty': return <EmptyCard data={payload} />;
    default: return <p className="text-xs text-[#888]">Tipo no soportado: {payload.type}</p>;
  }
}

function SqlBlock({ sql }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(sql);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <div className="rounded-lg border border-[#E8E8E7] bg-[#faf9f7]" data-testid="sql-block">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#f0ebe5] transition-colors"
        data-testid="sql-toggle"
      >
        <span className="text-xs font-semibold text-[#5A5A5A] flex items-center gap-1.5">
          <Code2 size={12} /> Ver SQL generado
        </span>
        {open ? <ChevronDown size={14} className="text-[#888]" /> : <ChevronUp size={14} className="text-[#888]" />}
      </button>
      {open && (
        <div className="px-3 pb-3">
          <div className="flex justify-end mb-1">
            <button onClick={handleCopy} className="text-[10px] text-[#9d8272] hover:underline flex items-center gap-1" data-testid="sql-copy">
              <Copy size={10} /> {copied ? '¡Copiado!' : 'Copiar'}
            </button>
          </div>
          <pre className="text-[11px] font-mono text-[#2D2D2D] bg-white border border-[#E8E8E7] rounded p-2 whitespace-pre-wrap break-all max-h-60 overflow-auto">{sql}</pre>
        </div>
      )}
    </div>
  );
}

function ChatBubble({ message }) {
  const isUser = message.role === 'user';
  const isLlm = message.route === 'llm';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`} data-testid={`chat-bubble-${message.role}`}>
      <div className={`max-w-[85%] sm:max-w-[75%] ${isUser ? '' : 'w-full sm:w-auto'}`}>
        <div className={`rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-[#9d8272] text-white rounded-br-md'
            : 'bg-white border border-[#E8E8E7] rounded-bl-md shadow-sm'
        }`}>
          {isUser ? (
            <p className="text-sm">{message.text}</p>
          ) : (
            <div className="space-y-3">
              {/* Explanation del LLM (Ruta B) */}
              {isLlm && message.explanation && (
                <p className="text-sm italic text-[#5A5A5A]" data-testid="llm-explanation">{message.explanation}</p>
              )}
              {message.payload && <ResponseRenderer payload={message.payload} />}
              {message.text && !message.payload && (
                <p className="text-sm text-[#2D2D2D]" dangerouslySetInnerHTML={{
                  __html: message.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
                }} />
              )}
              {/* Chip cross-project */}
              {isLlm && message.cross_project_used && (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-medium" data-testid="cross-project-chip">
                  <Globe2 size={10} /> Consulta cross-proyecto
                </span>
              )}
              {/* SQL solo para Administradores */}
              {isLlm && message.sql && <SqlBlock sql={message.sql} />}
              {(message.payload || isLlm) && (
                <div className="flex items-center justify-between pt-2 border-t border-[#f0f0f0]">
                  <span className="text-[10px] text-[#aaa] flex items-center gap-1">
                    <Clock size={10} />
                    {message.timestamp}
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                    isLlm ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'
                  }`}>
                    {isLlm ? 'IA' : 'Comando'}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        {!message.payload && !isLlm && (
          <p className={`text-[10px] mt-1 ${isUser ? 'text-right' : 'text-left'} text-[#aaa]`}>
            {message.timestamp}
          </p>
        )}
      </div>
    </div>
  );
}

function DisclaimerBubble({ text }) {
  return (
    <div className="flex justify-start mb-4" data-testid="chat-bubble-disclaimer">
      <div className="max-w-[85%] sm:max-w-[75%]">
        <div className="rounded-2xl rounded-bl-md px-4 py-3 bg-red-50 border border-red-200 shadow-sm">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700">{text}</p>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============ CommandsCarousel ============
// Carrusel deslizante de chips de comandos rápidos.
// - PAGE_SIZE chips por página (configurable via COMMANDS_PAGE_SIZE)
// - Dots clickeables tipo iOS (pill alargado en activo)
// - Flechas laterales para navegación con mouse
// - Swipe táctil en mobile (threshold 50px)
// - Teclado: ←/→ cambian de página cuando el carrusel tiene focus
function CommandsCarousel({ commands, onCommandClick }) {
  const totalPages = Math.max(1, Math.ceil(commands.length / COMMANDS_PAGE_SIZE));
  const [currentPage, setCurrentPage] = useState(0);
  const touchStartX = useRef(null);

  // Pages: [[cmd1..cmd6], [cmd7..cmd10], ...]
  const pages = useMemo(() => {
    const out = [];
    for (let i = 0; i < commands.length; i += COMMANDS_PAGE_SIZE) {
      out.push(commands.slice(i, i + COMMANDS_PAGE_SIZE));
    }
    return out;
  }, [commands]);

  // Si la cantidad de páginas se reduce y el currentPage queda fuera de rango,
  // lo ajustamos (defensivo: si COMMANDS cambia en runtime).
  useEffect(() => {
    if (currentPage >= totalPages) setCurrentPage(totalPages - 1);
  }, [totalPages, currentPage]);

  const goPrev = () => setCurrentPage((p) => Math.max(0, p - 1));
  const goNext = () => setCurrentPage((p) => Math.min(totalPages - 1, p + 1));

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
  };

  const showNav = totalPages > 1;
  if (commands.length === 0) return null;

  return (
    <div
      className="relative"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      data-testid="commands-carousel"
    >
      {/* Track con páginas */}
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${currentPage * 100}%)` }}
        >
          {pages.map((pageItems, pageIdx) => (
            <div
              key={pageIdx}
              className="grid grid-cols-2 gap-2 px-4 pb-2 shrink-0 w-full"
              data-testid={`commands-page-${pageIdx}`}
              aria-hidden={pageIdx !== currentPage}
            >
              {pageItems.map((cmd) => (
                <button
                  key={cmd.id}
                  onClick={() => onCommandClick(cmd.id)}
                  title={cmd.tooltip || cmd.label}
                  className="flex items-center gap-2 px-3 py-2 bg-[#f8f6f3] hover:bg-[#f0ebe5] border border-[#E8E8E7] rounded-lg text-left transition-colors group"
                  data-testid={`cmd-${cmd.id.replace('/', '')}`}
                  tabIndex={pageIdx === currentPage ? 0 : -1}
                >
                  <span className="text-xs font-bold text-[#9d8272] shrink-0">{cmd.id}</span>
                  <span className="text-xs text-[#5A5A5A] group-hover:text-[#2D2D2D] leading-tight">{cmd.label}</span>
                </button>
              ))}
              {/* Padding invisible si la última página tiene menos de PAGE_SIZE
                  para mantener alineación visual y evitar saltos de altura */}
              {pageItems.length < COMMANDS_PAGE_SIZE &&
                Array.from({ length: COMMANDS_PAGE_SIZE - pageItems.length }).map((_, i) => (
                  <div key={`pad-${pageIdx}-${i}`} aria-hidden="true" />
                ))}
            </div>
          ))}
        </div>
      </div>

      {/* Flechas laterales (solo si hay más de 1 página) */}
      {showNav && (
        <>
          <button
            type="button"
            onClick={goPrev}
            disabled={currentPage === 0}
            aria-label="Página anterior"
            className="absolute left-0.5 top-1/2 -translate-y-1/2 -translate-x-1 p-1 rounded-full bg-white/90 border border-[#E8E8E7] hover:bg-[#f0ebe5] disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
            data-testid="commands-prev"
          >
            <ChevronLeft size={14} className="text-[#5A5A5A]" />
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={currentPage === totalPages - 1}
            aria-label="Página siguiente"
            className="absolute right-0.5 top-1/2 -translate-y-1/2 translate-x-1 p-1 rounded-full bg-white/90 border border-[#E8E8E7] hover:bg-[#f0ebe5] disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
            data-testid="commands-next"
          >
            <ChevronRight size={14} className="text-[#5A5A5A]" />
          </button>
        </>
      )}

      {/* Dots de paginación */}
      {showNav && (
        <div
          className="flex justify-center items-center gap-1.5 pb-2.5 pt-0.5"
          role="tablist"
          aria-label="Páginas de comandos"
        >
          {Array.from({ length: totalPages }).map((_, i) => {
            const isActive = i === currentPage;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setCurrentPage(i)}
                role="tab"
                aria-selected={isActive}
                aria-label={`Página ${i + 1} de ${totalPages}`}
                className={`h-1.5 rounded-full transition-all duration-200 ${
                  isActive
                    ? 'w-5 bg-[#9d8272]'
                    : 'w-1.5 bg-[#d4cdc4] hover:bg-[#9d8272]/60'
                }`}
                data-testid={`commands-dot-${i}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============ MAIN ============
export default function ChatbotComercial() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [context, setContext] = useState(null);
  const [showContextModal, setShowContextModal] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const [commandsExpanded, setCommandsExpanded] = useState(true);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Inicio', path: '/dashboard' },
      { label: 'G. Comercial', path: null },
      { label: 'Chatbot', path: '/chatbot-comercial' },
    ]);
    return () => setBreadcrumbs(null);
  }, [setBreadcrumbs]);

  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => setIsOffline(false);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Persistencia en sessionStorage
  useEffect(() => {
    const saved = sessionStorage.getItem('chatbot_context_v2');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setContext(parsed);
        setShowContextModal(false);
      } catch { /* ignore */ }
    }
    const savedMsgs = sessionStorage.getItem('chatbot_messages_v2');
    if (savedMsgs) {
      try { setMessages(JSON.parse(savedMsgs)); } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    if (context) sessionStorage.setItem('chatbot_context_v2', JSON.stringify(context));
  }, [context]);

  useEffect(() => {
    if (messages.length > 0) sessionStorage.setItem('chatbot_messages_v2', JSON.stringify(messages));
  }, [messages]);

  const getNow = () => new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });

  const handleConfirmContext = (ctx) => {
    setContext(ctx);
    setShowContextModal(false);
    const projName = ctx.project?.nombre_proyecto || ctx.project?.codigo_proyecto;
    const stageName = ctx.stage?.nombre_etapa ? ` · ${ctx.stage.nombre_etapa}` : '';
    setMessages([{
      id: Date.now(),
      role: 'assistant',
      text: `Hola! Estoy listo para ayudarte con información de **${projName}${stageName}**. Puedes usar los comandos rápidos o escribirme una consulta.`,
      timestamp: getNow(),
    }]);
  };

  const handleChangeContext = () => setShowContextModal(true);

  const [isLlmThinking, setIsLlmThinking] = useState(false);

  const processMessage = useCallback(async (text) => {
    const cmd = text.trim();
    if (!cmd) return;
    if (!context?.project?.codigo_proyecto) {
      toast.error('Selecciona proyecto y etapa primero.');
      return;
    }

    const userMsg = { id: Date.now(), role: 'user', text: cmd, timestamp: getNow() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');

    const isLlm = !cmd.startsWith('/');
    if (isLlm) setIsLlmThinking(true);
    else setIsTyping(true);

    const token = localStorage.getItem('token');
    try {
      let data;
      if (isLlm) {
        // Ruta B - LLM
        const r = await axios.post(`${API}/api/chatbot/chat`, {
          question: cmd,
          codigo_proyecto: context.project.codigo_proyecto,
          codigo_etapa: context.stage?.codigo_etapa || null,
        }, { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 });
        data = r.data;
        const baseTs = Date.now() + 1;
        const newMessages = [{
          id: baseTs,
          role: 'assistant',
          route: 'llm',
          payload: data.payload,
          explanation: data.explanation,
          cross_project_used: data.cross_project_used,
          sql: data.sql || null,  // solo si Admin
          timestamp: getNow(),
        }];
        setMessages((prev) => [...prev, ...newMessages]);
      } else {
        // Ruta A - Comando /N
        const r = await axios.post(`${API}/api/chatbot/command`, {
          command: cmd,
          codigo_proyecto: context.project.codigo_proyecto,
          codigo_etapa: context.stage?.codigo_etapa || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
        data = r.data;
        const payload = data.payload;
        const baseTs = Date.now() + 1;
        const newMessages = [{
          id: baseTs,
          role: 'assistant',
          payload,
          timestamp: getNow(),
        }];
        if (payload?.disclaimer_meta_faltante && payload?.disclaimer_text) {
          newMessages.push({
            id: baseTs + 1,
            role: 'assistant_disclaimer',
            text: payload.disclaimer_text,
            timestamp: getNow(),
          });
        }
        setMessages((prev) => [...prev, ...newMessages]);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || 'Error desconocido';
      setMessages((prev) => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        text: `❌ ${detail}`,
        timestamp: getNow(),
      }]);
    } finally {
      setIsTyping(false);
      setIsLlmThinking(false);
    }
  }, [context]);

  const handleSend = () => {
    if (isOffline || !input.trim() || isTyping || isLlmThinking) return;
    processMessage(input);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCommandClick = (cmdId) => processMessage(cmdId);

  const toggleVoice = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Tu navegador no soporta reconocimiento de voz.');
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'es-PE';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript.toLowerCase().trim();
      const mapped = VOICE_NUMBER_MAP[transcript];
      if (mapped) processMessage(mapped);
      else if (/^\/[1-8]/.test(transcript)) processMessage(transcript);
      else { setInput(transcript); setTimeout(() => processMessage(transcript), 1500); }
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const contextLabel = context
    ? `${context.project?.nombre_proyecto}${context.stage ? ' · ' + context.stage.nombre_etapa : ''}`
    : '';

  return (
    <div className="flex flex-col -m-3 sm:-m-4 lg:-m-6 xl:-m-8 h-[calc(100vh-64px)] overflow-hidden" data-testid="chatbot-page">
      {showContextModal && (
        <ContextModal
          onClose={() => setShowContextModal(false)}
          onConfirm={handleConfirmContext}
        />
      )}

      {context && !showContextModal && (
        <div className="flex items-center justify-between bg-[#f8f6f3] border-b border-[#E8E8E7] px-4 py-2.5 shrink-0" data-testid="context-banner">
          <div className="flex items-center gap-2">
            <Building2 size={16} className="text-[#9d8272]" />
            <span className="text-sm font-medium text-[#2D2D2D]">{contextLabel}</span>
          </div>
          <button
            onClick={handleChangeContext}
            className="text-xs text-[#9d8272] hover:underline font-medium flex items-center gap-1"
            data-testid="change-context-btn"
          >
            <RefreshCw size={12} /> Cambiar
          </button>
        </div>
      )}

      {isOffline && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-center">
          <p className="text-xs text-red-600 font-medium">Sin conexión a internet. El chat está deshabilitado temporalmente.</p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-4 bg-[#faf9f7]" data-testid="chat-messages">
        {!context && !showContextModal && (
          <div className="flex items-center justify-center h-full" data-testid="empty-no-context">
            <div className="text-center max-w-sm px-6">
              <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-[#9d8272] to-[#8a7162] flex items-center justify-center shadow-lg">
                <Building2 size={36} className="text-white" />
              </div>
              <h3
                className="text-xl font-bold text-[#2D2D2D] mb-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Selecciona un proyecto para empezar
              </h3>
              <p className="text-sm text-[#5A5A5A] mb-6 leading-relaxed">
                Necesito conocer el contexto comercial (proyecto y etapa) antes de
                responder tus consultas o ejecutar comandos rápidos.
              </p>
              <Button
                onClick={handleChangeContext}
                className="bg-[#9d8272] hover:bg-[#8a7162] text-white px-5 py-2.5 shadow-md"
                data-testid="open-context-modal-btn"
              >
                <Building2 size={16} className="mr-2" /> Establecer contexto
              </Button>
            </div>
          </div>
        )}

        {context && messages.length === 0 && !showContextModal && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center opacity-50">
              <MessageSquare size={48} className="mx-auto mb-3 text-[#9d8272]" />
              <p className="text-sm text-[#888]">Escribe una pregunta o usa los comandos rápidos</p>
            </div>
          </div>
        )}

        {messages.map((msg) =>
          msg.role === 'assistant_disclaimer'
            ? <DisclaimerBubble key={msg.id} text={msg.text} />
            : <ChatBubble key={msg.id} message={msg} />
        )}

        {(isTyping || isLlmThinking) && (
          <div className="flex justify-start mb-4" data-testid="typing-indicator">
            <div className="bg-white border border-[#E8E8E7] rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-[#9d8272] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-[#9d8272] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-[#9d8272] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                {isLlmThinking && (
                  <span className="text-xs text-[#9d8272] font-medium" data-testid="llm-thinking-label">
                    Pensando…
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {context && !showContextModal && (
        <div className="shrink-0 border-t border-[#E8E8E7] bg-white" data-testid="commands-grid">
          <button
            onClick={() => setCommandsExpanded((p) => !p)}
            className="w-full flex items-center justify-between px-4 py-2 hover:bg-[#faf9f7] transition-colors"
            data-testid="commands-toggle"
          >
            <span className="text-xs font-semibold text-[#888] flex items-center gap-1">
              <Zap size={12} /> Comandos rápidos
            </span>
            {commandsExpanded ? <ChevronDown size={14} className="text-[#888]" /> : <ChevronUp size={14} className="text-[#888]" />}
          </button>
          {commandsExpanded && (
            <CommandsCarousel commands={COMMANDS} onCommandClick={handleCommandClick} />
          )}
        </div>
      )}

      {context && !showContextModal && (
        <div className="shrink-0 border-t border-[#E8E8E7] bg-white p-3 sm:p-4" data-testid="chat-input-area">
          <div className="flex items-center gap-2">
            <button
              onClick={toggleVoice}
              className={`p-2.5 rounded-full transition-all shrink-0 ${
                isListening
                  ? 'bg-red-500 text-white animate-pulse'
                  : 'bg-[#f8f6f3] text-[#9d8272] hover:bg-[#f0ebe5]'
              }`}
              data-testid="voice-btn"
            >
              {isListening ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isOffline ? 'Sin conexión...' : 'Escribe un comando (/1...) o haz una pregunta'}
              disabled={isOffline || isTyping || isLlmThinking}
              className="flex-1 px-4 py-2.5 bg-[#f8f6f3] border border-[#E8E8E7] rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#9d8272] focus:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isOffline || isTyping || isLlmThinking}
              className="bg-[#9d8272] hover:bg-[#8a7162] text-white rounded-full p-2.5 shrink-0"
              data-testid="send-btn"
            >
              <Send size={18} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
